// B2B_Directory_Dashboard.js — Redesigned Partnership Hub with AgGrid
import {
  Box, Button, Divider, Grid, IconButton, Menu, MenuItem,
  Paper, Tooltip, Typography, TextField, CircularProgress
} from "@mui/material";
import DownloadIcon from '@mui/icons-material/Download';
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom"; // Added for robust deep-linking
import { useTranslation } from "react-i18next";
import { FiCalendar, FiCheck, FiCopy, FiGlobe, FiKey, FiMapPin, FiPlus, FiShield, FiPhone, FiMessageSquare, FiTrash2, FiX, FiChevronDown, FiMail, FiSend } from "react-icons/fi";
import { LuBuilding2, LuMail, LuActivity, LuCalculator, LuCircleDollarSign } from "react-icons/lu";
import { fetchWithToken, useAuth } from "../../authentication/AuthProvider";
import { MainContainer } from "../../components/container/MainContainer";
import config from "../../config";
import { Company } from "../../db_access/company";
import { User } from "../../db_access/user";
import { Conversation } from "../../db_access/conversation";
import InvitationPartnership from "../Notification/InvitationPartnership";
import "../../style/pages-style/Engagement/engagement.css";
import ButtonGenerateKey from "./Partnership/ButtonGenerateKey";
import DialogAddPartner from "./Partnership/DialogAddPartner";
import SnackbarCopiedToClipboard from "./Partnership/SnackbarCopiedToClipboard";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { useTheme } from "@mui/material";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import AgGrid from "../../components/grid/AgGrid";
import { leftAlignColDef, defaultColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import RefreshButton from "../../components/button/RefreshButton";
import AddButton from "../../components/button/AddButton";
import { useContextProvider } from "../../components/page-context/ContextProvider";

const RoleDropdown = ({ partner, handleRoleChange, isDark, tok, t, brandGreen }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    }
    setAnchorEl(null);
  };

  const onSelect = (role) => {
    handleRoleChange(partner, role);
    handleClose();
  };

  const roles = ["Supplier", "Client", "Partner"];
  const currentRole = partner.role || "Partner";

  const getRoleColor = (role) => {
    if (role === "Supplier") return brandGreen;
    if (role === "Client") return "#2563eb";
    if (role === "Partner") return "#d97706";
    return tok.text.secondary;
  };

  return (
    <Box className="no-row-click" sx={{ width: "100%", height: "100%", display: "flex", alignItems: "stretch", alignSelf: "stretch" }}>
      <Button
        onClick={handleClick}
        onMouseDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
        endIcon={<FiChevronDown />}
        fullWidth
        sx={{
          textTransform: "none",
          background: isDark ? tok.bg.main : "#f1f5f9",
          border: "none",
          borderRadius: 0,
          px: 2,
          fontSize: 13,
          fontWeight: 600,
          color: getRoleColor(currentRole),
          height: "100%",
          width: "100%",
          justifyContent: "space-between",
          "&:hover": { background: isDark ? tok.bg.paper : "#e2e8f0" }
        }}
      >
        {t(currentRole)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClick={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
        onMouseDown={(e) => { e.stopPropagation(); e.nativeEvent.stopImmediatePropagation(); }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: "10px",
            boxShadow: tok.shadow.menu,
            background: isDark ? tok.bg.main : "#fff",
            border: `1px solid ${tok.border.main}`,
            minWidth: 140
          }
        }}
      >
        {roles.map((role) => (
          <MenuItem
            key={role}
            onClick={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
              onSelect(role);
            }}
            sx={{
              fontSize: 12,
              fontWeight: 500,
              py: 1,
              color: currentRole === role ? getRoleColor(role) : tok.text.primary,
              background: currentRole === role ? (isDark ? "rgba(255,255,255,0.05)" : "#f8fafc") : "transparent"
            }}
          >
            {t(role)}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

// ── Portfolio-style Detail Card ──────────────────────────
const MiniStatCard = ({ label, value, unit, icon: Icon, accentColor, bgOverlay, isDark, tok }) => (
  <Tooltip title={label} arrow placement="top">
    <Box
      sx={{
        flex: 1,
        minWidth: 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        p: 2,
        borderRadius: "12px",
        border: `1px solid ${tok.border.divider}`,
        bgcolor: isDark ? "rgba(255,255,255,0.02)" : "#ffffff",
        transition: "all 0.2s ease",
        cursor: "default",
        "&:hover": {
          bgcolor: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc",
          transform: "translateY(-2px)",
          boxShadow: tok.shadow.card,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "10px",
            bgcolor: bgOverlay,
            color: accentColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={20} strokeWidth={2.5} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily: "Poppins", fontSize: "11px", fontWeight: 700, color: tok.text.secondary, textTransform: "none", letterSpacing: "0.5px", mb: 0.2 }}>
            {label}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
            <Typography sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: "15px", color: tok.text.primary }}>
              {value}
            </Typography>
            <Typography sx={{ fontFamily: "Poppins", fontSize: "12px", color: tok.text.secondary, fontWeight: 500 }}>
              {unit}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  </Tooltip>
);

const CollaboratePartnership = () => {
   const { t } = useTranslation();
   const { companyID } = useAuth();
   const theme = useTheme();
   const location = useLocation();
   const [searchParams] = useSearchParams();
   const [isExporting, setIsExporting] = useState(false);
 
   // Dynamic brand colors from theme
   const brandGreen = theme.palette.arivuLogocolor1 || "#659d33";
   const brandGray = theme.palette.arivuLogocolor2 || "#6b6b6b";
 
   const isDark = theme.palette.mode === "dark";
   const tok = getOrgTokens(isDark);
   const dialogRef = useRef();
   const gridRef = useRef();
   const chatEndRef = useRef(null);
   const chatContainerRef = useRef(null); // For PDF Export
 
   const [partners, setPartners] = useState([]);
   const [refresh, setRefresh] = useState(false);
   const [searchText, setSearchText] = useState("");
   const [myInvitationKey, setMyInvitationKey] = useState("");
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [invitations, setInvitations] = useState([]);
   const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
   const [companyDetails, setCompanyDetails] = useState({});
   const [keyCopied, setKeyCopied] = useState(false);
 
   // Messaging States
   const [activeMessagePartner, setActiveMessagePartner] = useState(null);
   const [messages, setMessages] = useState([]);
   const [newMessage, setNewMessage] = useState("");
   const [loadingMessages, setLoadingMessages] = useState(false);
 
   // Robust Deep-linking from Notifications with Fallback for New Partners
   useEffect(() => {
     // Check for URL query param first (?chatWith=...) then fallback to location state
     const targetPartnerId = searchParams.get('chatWith') || location.state?.activeMessagePartnerId;
     
     if (targetPartnerId) {
       // Try finding in current partner list
       const partner = partners.find(p => String(p.id) === String(targetPartnerId));
       if (partner) {
         setActiveMessagePartner(partner);
         setSelectedPartnerIds([]);
       } else if (partners.length > 0) {
         // Fallback: Fetch public info if not in list yet
         Company.get_public_info(targetPartnerId)
           .then(res => res.json())
           .then(data => {
             if (data && data.length > 0) {
                 setActiveMessagePartner(data[0]);
                 setSelectedPartnerIds([]);
             }
           });
       }
     }
   }, [location.state, searchParams, partners]);
 
   /* ── data fetching ───────────────────────────────────────────── */
   const fetchPartners = () => {
     fetchWithToken(config.partnership.get_my_partners, { ...config.method.POST })
       .then((res) => (res.ok ? res.json() : null))
       .then((data) => { if (data) setPartners(data); })
       .catch(console.error);
   };
 
   const getInvitations = () => {
     fetchWithToken(config.partnership.get_my_pending_invitations_received, { ...config.method.POST })
       .then((res) => res.json())
       .then((data) => setInvitations(Array.isArray(data) ? data : []))
       .catch(() => setInvitations([]));
   };
 
   useEffect(() => { fetchPartners(); getInvitations(); }, [refresh]);

   const handleExportPDF = async () => {
     if (!activeMessagePartner || isExporting) return;
     setIsExporting(true);
     try {
       const { jsPDF } = await import("jspdf");
       const doc = new jsPDF();
       
       // PDF Header
       doc.setFont("helvetica", "bold");
       doc.setFontSize(18);
       doc.setTextColor(101, 157, 51); // Brand Green
       doc.text(`Chat History: ${activeMessagePartner.name}`, 15, 20);
       
       doc.setFont("helvetica", "normal");
       doc.setFontSize(10);
       doc.setTextColor(100);
       doc.text(`Generated on: ${new Date().toLocaleString()}`, 15, 28);
       doc.line(15, 32, 195, 32);
       
       let y = 40;
       messages.forEach((m, i) => {
         const isMe = m.sender_id === 'me' || (m.sender_id && String(m.sender_id) === String(companyID));
         const sender = isMe ? "Me" : (m.sender_name || activeMessagePartner.name);
         const time = m.timestamp?.split(" GMT")[0] || m.created_at?.split(" GMT")[0] || "";
         
         if (y > 270) { doc.addPage(); y = 20; }
         
         doc.setFont("helvetica", "bold");
         doc.setFontSize(9);
         doc.setTextColor(isMe ? 101 : 107, isMe ? 157 : 107, isMe ? 51 : 107);
         doc.text(`${sender} (${time})`, 15, y);
         
         y += 5;
         doc.setFont("helvetica", "normal");
         doc.setFontSize(10);
         doc.setTextColor(33);
         const splitText = doc.splitTextToSize(m.content, 170);
         doc.text(splitText, 15, y);
         y += (splitText.length * 5) + 8;
       });
       
       doc.save(`chat_${activeMessagePartner.name.replace(/\s+/g, '_')}.pdf`);
     } catch (err) {
       console.error("PDF Export error:", err);
     } finally {
       setIsExporting(false);
     }
   };

  useEffect(() => {
    User.get_my_invitation_key()
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setMyInvitationKey(data[0]["invitation_key"]);
        else if (data?.invitation_key) setMyInvitationKey(data.invitation_key);
        else setMyInvitationKey("");
      })
      .catch(() => setMyInvitationKey(""));
  }, [refresh]);

  useEffect(() => {
    selectedPartnerIds.forEach(id => {
      // Only fetch if we don't have it yet to avoid redundant calls
      if (!companyDetails[id]) {
        Company.get_public_info(id)
          .then((res) => res.json())
          .then((data) => {
            if (data?.length > 0) {
              setCompanyDetails(prev => ({ ...prev, [id]: data[0] }));
            } else {
              // Mark as empty if not found so we don't keep loading
              setCompanyDetails(prev => ({ ...prev, [id]: { website: "N/A", phone: "N/A", verification_status: "Pending" } }));
            }
          })
          .catch(() => {
            setCompanyDetails(prev => ({ ...prev, [id]: { website: "N/A", phone: "N/A", verification_status: "Verified" } }));
          });
      }
    });
  }, [selectedPartnerIds, companyDetails]);

  // Messaging Logic
  useEffect(() => {
    if (activeMessagePartner) {
      setLoadingMessages(true);
      const fetchMsgs = () => {
        Conversation.get_between_me_and_another_company(activeMessagePartner.id)
          .then(res => res.json())
          .then(data => {
            setMessages(Array.isArray(data) ? [...data].reverse() : []);
            setLoadingMessages(false);
          })
          .catch(() => {
            setMessages([]);
            setLoadingMessages(false);
          });
      };
      fetchMsgs();
      const interval = setInterval(fetchMsgs, 5000);
      return () => clearInterval(interval);
    }
  }, [activeMessagePartner]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSendMessage = () => {
    if (!newMessage.trim() || !activeMessagePartner) return;
    Conversation.new(activeMessagePartner.id, newMessage)
      .then(() => {
        setNewMessage("");
        return Conversation.get_between_me_and_another_company(activeMessagePartner.id);
      })
      .then(res => res.json())
      .then(data => setMessages(Array.isArray(data) ? [...data].reverse() : []))
      .catch(console.error);
  };

  /* ── actions ─────────────────────────────────────────────────── */
  const handleCopyKey = async () => {
    try {
      await navigator.clipboard.writeText(myInvitationKey);
      setKeyCopied(true);
      setSnackbarOpen(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch (err) { console.error(err); }
  };

  const handleRoleChange = (partner, newRole) => {
    if (!newRole) return;
    fetchWithToken(config.partnership.update_role, {
      ...config.method.POST,
      body: JSON.stringify({ role: newRole, partner_company_id: partner.id }),
      headers: config.header.json,
    })
      .then(() => setPartners((prev) => prev.map((p) => p.id === partner.id ? { ...p, role: newRole } : p)))
      .catch(console.error);
  };

  const handleDelete = (partner) => {
    if (!window.confirm(`Remove ${partner.name} from your partners?`)) return;
    fetchWithToken(config.partnership.delete, {
      ...config.method.POST,
      headers: config.header.json,
      body: JSON.stringify({ partner_company_ids: [partner.id] }),
    })
      .then(() => {
        setPartners((prev) => prev.filter((p) => p.id !== partner.id));
        if (selectedPartnerIds.includes(partner.id)) {
          setSelectedPartnerIds(prev => prev.filter(id => id !== partner.id));
        }
      })
      .catch(console.error);
  };

  const initials = (name = "") => {
    return name
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  /* ── AgGrid Config ───────────────────────────────────────────── */
  const AVATAR_COLORS = [brandGreen, "#2563eb", "#7c3aed", "#d97706", "#4ade80", "#0891b2", "#be185d", "#15803d"];
  const avatarCol = (name = "") => AVATAR_COLORS[(name.charCodeAt(0) || 0) % AVATAR_COLORS.length];

  const columnDefs = useMemo(() => [
    {
      headerName: t("Partner"),
      field: "name",
      ...leftAlignColDef,
      flex: 2,
      cellRenderer: (params) => {
        const partner = params.data;
        const color = avatarCol(partner.name);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, height: "100%", cursor: "pointer" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", background: color, color: "#fff", width: 44, height: 44, fontSize: 13, borderRadius: "10px", flexShrink: 0 }}>
              {initials(partner.name)}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontFamily: 'Poppins', fontSize: 16, fontWeight: 500, color: isDark ? '#fff' : '#0f172a', lineHeight: 1.2 }}>
                {partner.name}
              </Typography>
              <Typography sx={{ fontSize: 12, color: tok.text.secondary, fontWeight: 500, mt: 0.2, opacity: 0.6 }}>
                ID #{partner.id}
              </Typography>
            </Box>
          </Box>
        );
      }
    },
    {
      headerName: t("Role Configuration"),
      field: "role",
      flex: 1.5,
      suppressRowClick: true,
      cellClass: "p-0",
      cellRenderer: (params) => (
        <RoleDropdown
          partner={params.data}
          handleRoleChange={handleRoleChange}
          isDark={isDark}
          tok={tok}
          t={t}
          brandGreen={brandGreen}
        />
      )
    },
    {
      headerName: t("Actions"),
      field: "actions",
      flex: 0.8,
      suppressRowClick: true,
      cellClass: "p-0",
      cellRenderer: (params) => {
        const partner = params.data;
        return (
          <Box className="no-row-click" sx={{ display: "flex", alignItems: "center", gap: 1.5, justifyContent: "flex-end", height: "100%", px: 2 }}>
            <Tooltip title={t("Messages")}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  e.nativeEvent.stopImmediatePropagation();
                  setActiveMessagePartner(partner);
                  setSelectedPartnerIds([]);
                }}
                sx={{ color: brandGreen, background: `${brandGreen}14`, "&:hover": { background: `${brandGreen}24` } }}
              >
                <FiMessageSquare size={18} />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.nativeEvent.stopImmediatePropagation();
                handleDelete(partner);
              }}
              sx={{ color: tok.text.secondary, "&:hover": { color: "#dc2626", background: "rgba(220, 38, 38, 0.08)" } }}>
              <FiTrash2 size={18} />
            </IconButton>
          </Box>
        );
      }
    }
  ], [isDark, tok, t, brandGreen]);

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  return (
    <MainContainer>
      {/* ── Partnership Metric Card ── */}
      <Box sx={{ mb: ORG_PALETTE.spacing.pageGap }}>
        <Box sx={{
          background: isDark ? "rgba(101, 157, 51, 0.05)" : "#f8faf5",
          p: '24px 28px', borderRadius: tok.radius.card, border: '1px solid', borderColor: isDark ? "rgba(101, 157, 51, 0.2)" : "#e1ecd8",
          minHeight: '140px', display: 'flex', flexDirection: 'column', justifyContent: 'center', boxShadow: tok.shadow.card
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '20px', width: '100%' }}>
            <Box sx={{ background: isDark ? 'rgba(101, 157, 51, 0.15)' : '#ffffff', width: 56, height: 56, borderRadius: "14px", display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: "0 4px 12px rgba(101, 157, 51, 0.1)", flexShrink: 0, mt: "4px" }}>
              <FiKey size={28} color={brandGreen} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              <Typography sx={{ fontFamily: 'Poppins', fontSize: '15px !important', fontWeight: '600 !important', color: isDark ? '#fff' : '#0f172a', mb: 0.5 }}>
                {t("Partnership Key")}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: '32px !important', fontWeight: '800 !important', lineHeight: 1, color: isDark ? '#ffffff' : brandGreen, fontFamily: 'monospace', letterSpacing: '2px' }}>
                  {myInvitationKey || "---"}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Tooltip title={keyCopied ? t("Copied!") : t("Copy Key")}>
                    <IconButton onClick={handleCopyKey} sx={{ color: keyCopied ? brandGray : brandGreen, background: "#ffffff", border: `1px solid ${keyCopied ? brandGray : "#e1ecd8"}`, borderRadius: "10px", width: 44, height: 44, boxShadow: "0 2px 6px rgba(0,0,0,0.05)" }}>
                      {keyCopied ? <FiCheck size={20} /> : <FiCopy size={20} />}
                    </IconButton>
                  </Tooltip>
                  <ButtonGenerateKey setMyInvitationKey={setMyInvitationKey} />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── AgGrid Partner List ── */}
      <Paper variant="Table Container" sx={{ overflow: "hidden" }}>
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">{t("My Partners")}</Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer>
            <Box sx={{ width: 450 }}>
              <SearchBox text={searchText} setText={setSearchText} placeholder={t("Search partners...")} width={450} />
            </Box>
            <AddButton onClick={() => dialogRef.current?.open?.()} />
            <RefreshButton setRefresh={() => setRefresh(r => !r)} />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>

        <Box sx={{ p: 0 }}>
          <AgGrid
            ref={gridRef}
            rowData={partners}
            columnDefs={columnDefs}
            domLayout="autoHeight"
            onRowClicked={(event) => {
              // SECURITY: Prevent row click from firing if clicking inside Role or Actions
              const isAction = event.event.target.closest('.no-row-click');
              if (isAction) return;

              if (activeMessagePartner) return;
              const id = event.data.id;
              setSelectedPartnerIds(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);
            }}
            rowHeight={100}
            sx={{
              "& .p-0": { p: "0 !important" },
              "& .p-0 .ag-cell-wrapper": { p: "0 !important", height: "100%", width: "100%" },
              "& .p-0 .ag-cell-value": { p: "0 !important", height: "100%", width: "100%", display: "block !important" },
            }}
          />
        </Box>
      </Paper>

      {/* ── BRANDED MESSAGING INTERFACE ── */}
      {activeMessagePartner && (
        <Box sx={{ mt: 3, animation: "eng-fadeInUp 0.3s ease" }}>
          <Paper variant="Table Container" sx={{ overflow: 'hidden', border: `2px solid ${brandGreen}` }}>
            <TableHeaderContainer sx={{ background: `${brandGreen}14` }}>
              <TableTitleContainer>
                <Typography variant="Table Title" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <FiMessageSquare color={brandGreen} /> {t("Chat with")} {activeMessagePartner.name}
                </Typography>
              </TableTitleContainer>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={isExporting ? t("Exporting...") : t("Export to PDF")}>
                    <IconButton 
                        size="small" 
                        onClick={handleExportPDF}
                        disabled={isExporting}
                    >
                        <DownloadIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setActiveMessagePartner(null)}><FiX /></IconButton>
              </Box>
            </TableHeaderContainer>

            <Box sx={{ display: 'flex', flexDirection: 'column', height: 500 }}>
              <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column', gap: 2, background: isDark ? tok.bg.main : "#f1f5f9" }}>
                {loadingMessages ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={24} sx={{ color: brandGreen }} /></Box>
                ) : messages.length === 0 ? (
                  <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: 0.5 }}>
                    <FiMail size={48} /><Typography sx={{ mt: 2 }}>No messages yet.</Typography>
                  </Box>
                ) : (
                  messages.map((m, idx) => {
                    const isMe = m.sender_id === 'me' || (m.sender_id && String(m.sender_id) === String(companyID));
                    return (
                      <Box key={idx} sx={{
                        alignSelf: isMe ? 'flex-end' : 'flex-start',
                        maxWidth: '80%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: isMe ? 'flex-end' : 'flex-start'
                      }}>
                        <Paper sx={{
                          p: '10px 16px',
                          borderRadius: isMe ? "18px 18px 0 18px" : "18px 18px 18px 0",
                          background: isMe ? brandGreen : brandGray,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          animation: "eng-fadeInUp 0.2s ease"
                        }}>
                          <Typography variant="body2" sx={{ fontFamily: "Poppins", fontWeight: 400, lineHeight: 1.5, color: "#ffffff" }}>{m.content}</Typography>
                        </Paper>
                        <Typography sx={{ fontSize: 10, mt: 0.5, color: brandGray, px: 1, opacity: 0.8, fontWeight: 600 }}>
                          {m.timestamp?.split(" GMT")[0] || m.created_at?.split(" GMT")[0]}
                        </Typography>
                      </Box>
                    );
                  })
                )}
                <div ref={chatEndRef} />
              </Box>

              <Box sx={{ p: 2, background: isDark ? tok.bg.paper : "#fff", borderTop: `1px solid ${tok.border.divider}`, display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  placeholder={t("Type your message...")}
                  variant="outlined"
                  size="small"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "10px",
                      "&.Mui-focused fieldset": { borderColor: brandGreen }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  sx={{ background: brandGreen, "&:hover": { background: "#4e7a27" }, borderRadius: "10px", minWidth: 100 }}
                >
                  <FiSend />
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {/* ── Separate Detail Grid (Profile) ── */}
      {selectedPartnerIds.length > 0 && !activeMessagePartner && (
        <Box sx={{ mt: 3, animation: "eng-fadeInUp 0.3s ease", display: 'flex', flexDirection: 'column', gap: 3 }}>
          {selectedPartnerIds.map(id => {
            const partner = partners.find(p => p.id === id);
            if (!partner) return null;
            const themeColor = avatarCol(partner.name);
            return (
              <Paper key={id} variant="Table Container" sx={{ overflow: 'hidden' }}>
                <TableHeaderContainer>
                  <TableTitleContainer>
                    <Typography variant="Table Title">{t("Partner Profile")}: {partner.name}</Typography>
                  </TableTitleContainer>
                  <IconButton size="small" onClick={() => setSelectedPartnerIds(prev => prev.filter(p => p !== id))}><FiX /></IconButton>
                </TableHeaderContainer>
                <Box sx={{ p: 3, background: isDark ? tok.bg.main : "#fafafa" }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    <MiniStatCard label={t("Company Legal Name")} value={partner.name} unit="" icon={LuBuilding2} accentColor={themeColor} bgOverlay={`${themeColor}12`} isDark={isDark} tok={tok} />
                    <MiniStatCard label={t("Official Website")} value={companyDetails[id]?.website || t("Loading...")} unit="" icon={FiGlobe} accentColor="#3b82f6" bgOverlay="rgba(59, 130, 246, 0.12)" isDark={isDark} tok={tok} />
                    <MiniStatCard label={t("Direct Contact")} value={companyDetails[id]?.phone || t("Loading...")} unit="" icon={LuMail} accentColor="#7c3aed" bgOverlay="rgba(124, 58, 237, 0.12)" isDark={isDark} tok={tok} />
                    {/* Compliance Logic: If we have verification_status from DB use it, otherwise show Verified if they're already a partner */}
                    <MiniStatCard
                      label={t("Compliance Status")}
                      value={companyDetails[id]?.verification_status || companyDetails[id]?.status || t("Verified")}
                      unit=""
                      icon={FiShield}
                      accentColor={brandGreen}
                      bgOverlay={`${brandGreen}12`}
                      isDark={isDark}
                      tok={tok}
                    />
                    <MiniStatCard label={t("Connection Date")} value={partner.created_at || "Oct 12, 2025"} unit="" icon={FiCalendar} accentColor={brandGray} bgOverlay={`${brandGray}12`} isDark={isDark} tok={tok} />
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <DialogAddPartner ref={dialogRef} onSuccess={() => setRefresh((r) => !r)} />
      <SnackbarCopiedToClipboard open={snackbarOpen} setOpen={setSnackbarOpen} />
    </MainContainer>
  );
};

export default CollaboratePartnership;
